/* ───────────────────────────────────────────────────────────────────────────
   RavishaHub — Ravisha's command centre.

   Four sub-tabs:
   - Compose      Send a WhatsApp / email message to picked recipients.
   - Templates    Editable presets (welcome, profile-meeting nudge, deadline
                  reminder, etc.) Ravisha can drop into Compose with one tap.
   - Group pics   Upload one image each for Grade 10/11/12. Used by the bot
                  whenever a TUAP group is created.
   - History      Audit log of everything she's sent.

   All sends go through the ravishaQueue Firestore collection — the bot picks
   them up and does the actual WhatsApp work.
   ─────────────────────────────────────────────────────────────────────────── */

import { useState, useEffect, useMemo, useRef } from "react";
import { queueBulk, queueGroups, logSend, queueRemindersRun } from "./tuap";
import { sendMail } from "./mailer";
import { db } from "./firebase";
import {
  doc, setDoc, collection, onSnapshot, query, orderBy, limit, serverTimestamp,
} from "firebase/firestore";

const Y = "#F2B100", YD = "#8A6400";
const INK = "#1A1A1E", MID = "#5C5C66", SUB = "#9A9AA2";
const BORDER = "#E6E6E9", CARD = "#FFFFFF";
const GREEN = "#16A34A", WA = "#25D366";

const FONT = "'Archivo',-apple-system,sans-serif";

/* ── Compress an image to a base64 data URL under ~700 KB ── */
function compressImage(file, maxDim = 1400, quality = 0.78) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let w = img.width, h = img.height;
      if (w > maxDim || h > maxDim) {
        const r = Math.min(maxDim / w, maxDim / h);
        w = Math.round(w * r); h = Math.round(h * r);
      }
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Bad image")); };
    img.src = url;
  });
}
function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = () => rej(new Error("Could not read file"));
    r.readAsDataURL(file);
  });
}
function digitsOnly(p) { return (p || "").toString().replace(/\D/g, ""); }
function tsAgo(t) {
  if (!t) return "";
  const d = t.toDate ? t.toDate() : new Date(t);
  if (isNaN(d)) return "";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

const SECTION = { fontSize: 12, fontWeight: 800, color: SUB,
  fontFamily: "'Space Mono',monospace", textTransform: "uppercase",
  letterSpacing: ".06em", marginBottom: 8 };

/* ──────────────────────────────────────────────────────────────────────
   COMPOSE TAB
   ────────────────────────────────────────────────────────────────────── */
function ComposeTab({ students, templates, defaultText }) {
  const [text, setText] = useState(defaultText || "");
  const [audience, setAudience] = useState("students");
  // "students" | "moms" | "dads" | "parents" | "individuals" | "groups" | "manual"
  const [picked, setPicked] = useState([]);  // student ids when audience=individuals or groups
  const [channel, setChannel] = useState("whatsapp"); // whatsapp | email | both
  const [pickerQ, setPickerQ] = useState("");
  const [manualRaw, setManualRaw] = useState("");   // free-text phone(s) for manual audience
  const [manualName, setManualName] = useState(""); // single recipient name for {{name}} expansion
  const [media, setMedia] = useState(null);     // { dataUrl, name, type }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const mediaInput = useRef(null);

  useEffect(() => { if (defaultText !== undefined) setText(defaultText); }, [defaultText]);

  const filtered = useMemo(() => {
    const s = pickerQ.trim().toLowerCase();
    return students.filter(x => !s || (x.studentName || "").toLowerCase().includes(s)
      || (x.school || "").toLowerCase().includes(s));
  }, [students, pickerQ]);

  // Parse the manual entry box: split on commas/newlines/spaces, drop empties.
  const manualNumbers = useMemo(() => {
    return manualRaw.split(/[\s,;]+/).map(s => s.trim()).filter(s => s.replace(/\D/g, "").length >= 8);
  }, [manualRaw]);

  function compileRecipients() {
    if (audience === "manual") {
      // Each manual phone becomes a recipient; if Ravisha typed one phone +
      // a name, the {{name}} merge tag uses that name. Otherwise it stays
      // generic ("there").
      const recipients = manualNumbers.map((phone, i) => ({
        phone,
        name: (manualNumbers.length === 1 && manualName.trim()) ? manualName.trim() : "",
      }));
      return { kind: "people", phones: recipients, emails: [] };
    }
    if (audience === "groups") {
      const groups = (picked.length ? students.filter(s => picked.includes(s.id)) : students)
        .filter(s => s.studentName).map(s => `${s.studentName} (NM Squad TUAP)`);
      return { kind: "groups", groups };
    }
    if (audience === "individuals") {
      const sel = students.filter(s => picked.includes(s.id));
      return rosterFor(sel);
    }
    return rosterFor(students);

    function rosterFor(list) {
      const phones = []; const emails = [];
      for (const s of list) {
        const a = s.onbAnswers || {};
        const student = { phone: a.studentPhone || s.studentPhone || "", email: a.email || s.studentEmail || "", name: s.studentName };
        const mom = { phone: a.motherPhone || "", email: a.motherEmail || "", name: `${s.studentName || "Student"} (mother)` };
        const dad = { phone: a.fatherPhone || "", email: a.fatherEmail || "", name: `${s.studentName || "Student"} (father)` };
        if (audience === "students" || audience === "individuals") {
          if (student.phone) phones.push({ phone: student.phone, name: student.name });
          if (student.email) emails.push(student.email);
        }
        if (audience === "moms" || audience === "parents" || audience === "individuals") {
          if (mom.phone) phones.push({ phone: mom.phone, name: mom.name });
          if (mom.email) emails.push(mom.email);
        }
        if (audience === "dads" || audience === "parents" || audience === "individuals") {
          if (dad.phone) phones.push({ phone: dad.phone, name: dad.name });
          if (dad.email) emails.push(dad.email);
        }
      }
      // dedupe
      const seenP = new Set();
      const dedupedP = phones.filter(p => {
        const k = digitsOnly(p.phone);
        if (!k || seenP.has(k)) return false;
        seenP.add(k); return true;
      });
      return { kind: "people", phones: dedupedP, emails: [...new Set(emails)] };
    }
  }

  async function pickMedia(file) {
    const isImg = /^image\//.test(file.type);
    try {
      const dataUrl = isImg ? await compressImage(file, 1400, 0.78) : await fileToDataUrl(file);
      setMedia({ dataUrl, name: file.name, type: file.type });
    } catch (e) { alert(e.message || "Couldn't read file"); }
  }

  async function send() {
    setResult(null);
    if (!text.trim() && !media) { alert("Type a message or add an attachment."); return; }
    const r = compileRecipients();
    if (r.kind === "groups" && r.groups.length === 0) { alert("No groups in scope."); return; }
    if (r.kind === "people" && r.phones.length === 0 && r.emails.length === 0) {
      alert("No recipients matched the audience you picked.");
      return;
    }
    setBusy(true);
    try {
      let waCount = 0, emCount = 0, jobIds = [];
      // WhatsApp
      if (channel !== "email") {
        if (r.kind === "groups") {
          const ref = await queueGroups({ groupNames: r.groups, text,
            mediaDataUrl: media && media.dataUrl, mediaName: media && media.name });
          waCount = r.groups.length;
          jobIds.push(ref.id);
        } else {
          const ref = await queueBulk({ recipients: r.phones, text,
            mediaDataUrl: media && media.dataUrl, mediaName: media && media.name });
          waCount = r.phones.length;
          jobIds.push(ref.id);
        }
      }
      // Email — fan out via the existing Apps Script mailer.
      if (channel !== "whatsapp" && r.kind === "people" && r.emails.length) {
        const html = `<div style="font-family:Arial,sans-serif;font-size:15px;color:#222;line-height:1.6">${
          text.split("\n").map(l => l ? `<p>${l.replace(/</g, "&lt;")}</p>` : "<br/>").join("")
        }</div>`;
        for (const e of r.emails) {
          await sendMail({ to: e, subject: text.split("\n")[0].slice(0, 80) || "From NM Squad",
            html, fromName: "Ravisha · NM Squad" });
          emCount++;
        }
      }
      await logSend({
        kind: r.kind === "groups" ? "wa-group-bulk" : `wa-${audience}`,
        recipients: r.kind === "groups" ? r.groups : r.phones.map(p => p.phone),
        text, attachments: media ? [{ name: media.name, type: media.type }] : [],
        jobId: jobIds[0] || null,
        channel,
      });
      setResult({ waCount, emCount });
      setText("");
      setMedia(null);
    } catch (e) {
      alert("Send failed: " + e.message);
    } finally { setBusy(false); }
  }

  const r = compileRecipients();
  const audienceLabel = {
    students: "All students", moms: "All mothers", dads: "All fathers",
    parents: "All parents", individuals: `${picked.length} picked`,
    groups: picked.length ? `${picked.length} picked groups` : `All ${students.length} TUAP groups`,
    manual: `${manualNumbers.length} typed number${manualNumbers.length === 1 ? "" : "s"}`,
  }[audience];

  const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 18 };
  const chip = on => ({
    fontSize: 12, fontWeight: 800, padding: "6px 13px", borderRadius: 999,
    background: on ? INK : "#fff", color: on ? "#fff" : MID,
    border: `1.5px solid ${on ? INK : BORDER}`, cursor: "pointer",
  });

  return (
    <div style={{ display: "grid", gap: 18 }}>
      {/* Audience */}
      <div style={card}>
        <div style={SECTION}>Who is this for?</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            ["students", "All students"],
            ["moms", "All mothers"],
            ["dads", "All fathers"],
            ["parents", "All parents (mom + dad)"],
            ["individuals", "Pick specific people"],
            ["groups", "TUAP groups"],
            ["manual", "Type numbers manually"],
          ].map(([k, label]) => (
            <button key={k} onClick={() => setAudience(k)} style={chip(audience === k)}>
              {label}
            </button>
          ))}
        </div>
        {audience === "manual" && (
          <div style={{ marginTop: 12 }}>
            <textarea value={manualRaw} onChange={e => setManualRaw(e.target.value)}
              placeholder="Type one or more phone numbers, separated by commas or new lines.\nInclude the country code, e.g. +91 86930 75933"
              style={{ width: "100%", minHeight: 80, padding: "10px 12px", fontSize: 13.5,
                lineHeight: 1.5, fontFamily: "'Space Mono',monospace", color: INK,
                border: `1.5px solid ${BORDER}`, borderRadius: 9, outline: "none",
                resize: "vertical" }} />
            {manualNumbers.length === 1 && (
              <input value={manualName} onChange={e => setManualName(e.target.value)}
                placeholder="(Optional) Recipient's first name — used to expand {{name}} in the message"
                style={{ width: "100%", marginTop: 8, padding: "9px 12px", fontSize: 13,
                  fontWeight: 700, color: INK, border: `1.5px solid ${BORDER}`,
                  borderRadius: 9, outline: "none" }} />
            )}
            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700,
              color: manualNumbers.length ? GREEN : SUB }}>
              {manualNumbers.length} number{manualNumbers.length === 1 ? "" : "s"} ready
              {manualNumbers.length > 0 && `: ${manualNumbers.join(", ")}`}
            </div>
          </div>
        )}
        {(audience === "individuals" || audience === "groups") && (
          <div style={{ marginTop: 12 }}>
            <input value={pickerQ} onChange={e => setPickerQ(e.target.value)}
              placeholder="Search students…"
              style={{ width: "100%", padding: "9px 12px", fontSize: 13,
                border: `1px solid ${BORDER}`, borderRadius: 8 }} />
            <div style={{ maxHeight: 240, overflowY: "auto", marginTop: 10,
              border: `1px solid ${BORDER}`, borderRadius: 9 }}>
              {filtered.map(s => {
                const on = picked.includes(s.id);
                return (
                  <button key={s.id}
                    onClick={() => setPicked(p => on ? p.filter(x => x !== s.id) : [...p, s.id])}
                    style={{ display: "flex", alignItems: "center", gap: 10,
                      width: "100%", padding: "9px 13px", background: on ? "#FFF7DD" : "#fff",
                      border: "none", borderBottom: `1px solid ${BORDER}`,
                      cursor: "pointer", textAlign: "left" }}>
                    <span style={{ width: 16, height: 16, borderRadius: 4,
                      border: `2px solid ${on ? Y : BORDER}`,
                      background: on ? Y : "#fff", display: "flex",
                      alignItems: "center", justifyContent: "center" }}>
                      {on && (
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                          <path d="M5 13l4 4L19 7" stroke={YD} strokeWidth="5"
                            strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: INK }}>
                      {s.studentName || "Unnamed"}
                    </span>
                    <span style={{ fontSize: 11.5, color: SUB }}>
                      {s.grade || ""}{s.school ? " · " + s.school : ""}
                    </span>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div style={{ padding: 14, fontSize: 12.5, color: SUB }}>No students match.</div>
              )}
            </div>
          </div>
        )}
        <div style={{ marginTop: 12, fontSize: 12.5, color: MID }}>
          → <b>{audienceLabel}</b>
          {r.kind === "people" && ` · ${r.phones.length} WhatsApp, ${r.emails.length} email`}
          {r.kind === "groups" && ` · ${r.groups.length} groups`}
        </div>
      </div>

      {/* Channel */}
      <div style={card}>
        <div style={SECTION}>How should we deliver it?</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            ["whatsapp", "WhatsApp only"],
            ["email", "Email only"],
            ["both", "WhatsApp + email"],
          ].map(([k, label]) => (
            <button key={k} onClick={() => setChannel(k)} style={chip(channel === k)}>
              {label}
            </button>
          ))}
        </div>
        {audience === "groups" && channel !== "whatsapp" && (
          <div style={{ marginTop: 8, fontSize: 12, color: "#b45309", fontWeight: 700 }}>
            Email isn't supported for the "TUAP groups" audience — only WhatsApp will be sent.
          </div>
        )}
      </div>

      {/* Message body */}
      <div style={card}>
        <div style={SECTION}>Your message</div>
        {templates && templates.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {templates.map(t => (
              <button key={t.key} onClick={() => setText(t.body)}
                style={{ fontSize: 12, fontWeight: 700, padding: "6px 12px",
                  borderRadius: 999, background: "#FFF7DD", color: YD,
                  border: `1.5px solid ${BORDER}`, cursor: "pointer" }}>
                {t.label}
              </button>
            ))}
          </div>
        )}
        <textarea value={text} onChange={e => setText(e.target.value)}
          placeholder="Type the message. {{name}} expands to the recipient's first name."
          style={{ width: "100%", minHeight: 160, padding: "12px 14px",
            fontSize: 15, lineHeight: 1.55, fontFamily: FONT, color: INK,
            border: `1.5px solid ${BORDER}`, borderRadius: 10, outline: "none",
            resize: "vertical" }} />
        <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center",
          flexWrap: "wrap" }}>
          <input ref={mediaInput} type="file"
            accept="image/*,application/pdf,video/mp4,video/quicktime"
            style={{ display: "none" }}
            onChange={e => e.target.files[0] && pickMedia(e.target.files[0])} />
          <button onClick={() => mediaInput.current.click()}
            style={{ background: "#f1f1f4", color: MID, border: "none", borderRadius: 8,
              padding: "8px 14px", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
            {media ? "Replace attachment" : "Add image / PDF / video"}
          </button>
          {media && (
            <span style={{ fontSize: 12.5, color: MID, fontWeight: 700 }}>
              {media.name} <button onClick={() => setMedia(null)}
                style={{ marginLeft: 6, background: "none", border: "none", color: "#b91c1c",
                  fontSize: 12, fontWeight: 800, cursor: "pointer" }}>remove</button>
            </span>
          )}
          <span style={{ fontSize: 11.5, color: SUB }}>
            Images compress to ~700 KB; PDFs/videos under 1 MB go through as-is.
          </span>
        </div>
      </div>

      {/* Send */}
      <div>
        <button onClick={send} disabled={busy}
          style={{ background: WA, color: "#fff", border: "none", borderRadius: 11,
            padding: "13px 22px", fontSize: 14.5, fontWeight: 900, cursor: "pointer",
            opacity: busy ? 0.6 : 1, boxShadow: "0 6px 18px rgba(37,211,102,.32)" }}>
          {busy ? "Queueing…" :
            channel === "email" ? "Send email"
            : audience === "groups" ? "Post to groups"
            : "Send WhatsApp"
          }
        </button>
        {result && (
          <div style={{ marginTop: 10, fontSize: 13, fontWeight: 800, color: GREEN }}>
            Queued · {result.waCount} WhatsApp · {result.emCount} email
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   TEMPLATES TAB
   ────────────────────────────────────────────────────────────────────── */
const DEFAULT_TEMPLATES = [
  { key: "welcome",
    label: "Group welcome",
    body: "Welcome to {{name}}'s NM Squad TUAP group. Your Student Success " +
      "Officer will guide you through the next steps. Please make sure both " +
      "student and parents have joined and notifications are on." },
  { key: "profileMeeting",
    label: "Profile-meeting nudge",
    body: "Hi! Ravisha here from NM Squad. We'd love to schedule {{name}}'s " +
      "profile setup meeting this week. Could you share two time slots that " +
      "work for you?" },
  { key: "onbReminder",
    label: "Onboarding reminder",
    body: "Hi {{name}}, your TUAP onboarding form is open and saved up to " +
      "where you left off. Please log in and finish it when you can — every " +
      "section completed saves us hours later." },
  { key: "deadlineReminder",
    label: "Deadline reminder",
    body: "Heads up — your priority deadline is approaching. Please make sure " +
      "your essays and recommendations are on track. Reach out if you need help." },
  { key: "joinMissing",
    label: "Group-join chase",
    body: "Quick reminder to join the NM Squad TUAP group: {{link}}. We post all " +
      "updates here." },
];

function TemplatesTab({ templates, setTemplates }) {
  const [editing, setEditing] = useState(templates[0] && templates[0].key);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = templates.find(x => x.key === editing);
    if (t) setDraft({ ...t });
  }, [editing, templates]);

  async function save() {
    setBusy(true);
    try {
      const next = templates.map(t => t.key === draft.key ? draft : t);
      const map = {};
      next.forEach(t => { map[t.key] = { label: t.label, body: t.body }; });
      await setDoc(doc(db, "settings", "whatsapp_ravisha"),
        { templates: map, updatedAt: serverTimestamp() }, { merge: true });
      setTemplates(next);
    } finally { setBusy(false); }
  }

  const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 18 };

  return (
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "260px 1fr" }}>
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        {templates.map(t => (
          <button key={t.key} onClick={() => setEditing(t.key)}
            style={{ display: "block", width: "100%", padding: "12px 16px",
              background: editing === t.key ? "#FFF7DD" : "#fff", border: "none",
              borderBottom: `1px solid ${BORDER}`, cursor: "pointer", textAlign: "left",
              fontSize: 13.5, fontWeight: 800, color: INK }}>
            {t.label}
            <div style={{ fontSize: 11.5, fontWeight: 600, color: SUB, marginTop: 2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {t.body.slice(0, 50)}…
            </div>
          </button>
        ))}
      </div>
      <div style={card}>
        <div style={SECTION}>{draft.label || "Pick a template"}</div>
        <input value={draft.label || ""}
          onChange={e => setDraft(d => ({ ...d, label: e.target.value }))}
          placeholder="Template name"
          style={{ width: "100%", padding: "10px 13px", fontSize: 13.5, fontWeight: 700,
            border: `1.5px solid ${BORDER}`, borderRadius: 9, marginBottom: 10 }} />
        <textarea value={draft.body || ""}
          onChange={e => setDraft(d => ({ ...d, body: e.target.value }))}
          placeholder="Body. {{name}} expands to the student's first name when sent."
          style={{ width: "100%", minHeight: 200, padding: "12px 14px", fontSize: 13.5,
            lineHeight: 1.55, fontFamily: FONT, color: INK,
            border: `1.5px solid ${BORDER}`, borderRadius: 10, outline: "none",
            resize: "vertical" }} />
        <div style={{ marginTop: 12 }}>
          <button onClick={save} disabled={busy}
            style={{ background: Y, color: YD, border: "none", borderRadius: 9,
              padding: "9px 18px", fontSize: 13, fontWeight: 800, cursor: "pointer",
              opacity: busy ? 0.6 : 1 }}>
            {busy ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   GROUP PICTURES TAB
   ────────────────────────────────────────────────────────────────────── */
function GroupPicsTab({ pics, setPics }) {
  const [busy, setBusy] = useState({});

  async function pick(grade, file) {
    setBusy(b => ({ ...b, [grade]: true }));
    try {
      const dataUrl = await compressImage(file, 640, 0.86);
      const next = { ...pics, [grade]: dataUrl };
      await setDoc(doc(db, "settings", "whatsapp_ravisha"),
        { groupPics: next, updatedAt: serverTimestamp() }, { merge: true });
      setPics(next);
    } catch (e) {
      alert("Could not save image: " + e.message);
    } finally {
      setBusy(b => ({ ...b, [grade]: false }));
    }
  }

  function Tile({ grade, label }) {
    const url = pics[grade];
    const ref = useRef(null);
    return (
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12,
        padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        <input ref={ref} type="file" accept="image/*" style={{ display: "none" }}
          onChange={e => e.target.files[0] && pick(grade, e.target.files[0])} />
        <div style={{ aspectRatio: "1/1", borderRadius: 999, overflow: "hidden",
          background: "#F0F0F4", display: "flex", alignItems: "center",
          justifyContent: "center", border: `2px solid ${BORDER}` }}>
          {url ? (
            <img src={url} alt={label} style={{ width: "100%", height: "100%",
              objectFit: "cover" }} />
          ) : (
            <span style={{ fontSize: 12.5, color: SUB, fontWeight: 700 }}>
              No image yet
            </span>
          )}
        </div>
        <div style={{ fontSize: 14, fontWeight: 800, color: INK }}>{label}</div>
        <button onClick={() => ref.current.click()} disabled={busy[grade]}
          style={{ background: url ? "#f1f1f4" : Y, color: url ? MID : YD,
            border: "none", borderRadius: 8, padding: "9px 14px",
            fontSize: 12.5, fontWeight: 800, cursor: "pointer",
            opacity: busy[grade] ? 0.6 : 1 }}>
          {busy[grade] ? "Saving…" : url ? "Replace" : "Upload picture"}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 13.5, color: MID, marginBottom: 14, maxWidth: 640 }}>
        Upload one image per grade. The bot automatically sets the matching picture
        as the group's profile picture when a TUAP group is created — and you can
        push it to existing groups from the Live Status panel ("Send welcome + pic").
      </div>
      <div style={{ display: "grid", gap: 16,
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
        <Tile grade="grade10" label="Grade 10 groups" />
        <Tile grade="grade11" label="Grade 11 groups" />
        <Tile grade="grade12" label="Grade 12 groups" />
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   HISTORY TAB — log entries colour-coded by send result.

   green  = job completed cleanly
   red    = job done but error or all-recipients-failed
   gold   = job completed with mixed success
   grey   = pending (queue hasn't picked it up yet)
   ────────────────────────────────────────────────────────────────────── */
function HistoryTab() {
  const [entries, setEntries] = useState(null);
  const [jobs, setJobs] = useState({});  // jobId -> { done, error, result }

  useEffect(() => {
    const q = query(collection(db, "commsLog"), orderBy("at", "desc"), limit(80));
    return onSnapshot(q, snap => setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => setEntries([]));
  }, []);

  // Subscribe to the matching queue jobs so the History view updates
  // live as the bot processes each send.
  useEffect(() => {
    const q = query(collection(db, "ravishaQueue"), orderBy("requestedAt", "desc"), limit(150));
    return onSnapshot(q, snap => {
      const m = {};
      snap.docs.forEach(d => {
        const j = d.data();
        m[d.id] = { done: j.done, error: j.error, result: j.result };
      });
      setJobs(m);
    }, () => {});
  }, []);

  function resultFor(e) {
    if (!e.jobId) return { tone: "grey", label: "no job linked" };
    const j = jobs[e.jobId];
    if (!j) return { tone: "grey", label: "loading…" };
    if (!j.done) return { tone: "gold", label: "Pending" };
    // For bulk jobs: result is an array of per-recipient ok/fail.
    if (Array.isArray(j.result)) {
      const total = j.result.length;
      const okay = j.result.filter(r => r.ok).length;
      const failed = total - okay;
      if (failed === 0) return { tone: "green", label: `Sent to all ${total}` };
      if (okay === 0) return { tone: "red", label: `All ${total} failed` };
      return { tone: "mixed", label: `${okay} sent · ${failed} failed`, detail: j.result };
    }
    if (j.error) return { tone: "red", label: j.error };
    return { tone: "green", label: "Sent" };
  }

  if (!entries) return <div style={{ fontSize: 13, color: SUB }}>Loading…</div>;
  if (entries.length === 0) return (
    <div style={{ fontSize: 13.5, color: SUB, padding: 24, textAlign: "center",
      background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12 }}>
      Nothing sent yet. Your first message will show up here.
    </div>
  );

  const TONES = {
    green: { bg: "#F0FDF4", border: "#16A34A", fg: "#15803D", chip: "#16A34A" },
    red:   { bg: "#FEF2F2", border: "#DC2626", fg: "#B91C1C", chip: "#DC2626" },
    gold:  { bg: "#FEF9C3", border: "#CA8A04", fg: "#A16207", chip: "#CA8A04" },
    mixed: { bg: "#FFF7ED", border: "#EA580C", fg: "#C2410C", chip: "#EA580C" },
    grey:  { bg: CARD,      border: BORDER,    fg: SUB,       chip: SUB       },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {entries.map(e => {
        const r = resultFor(e);
        const tone = TONES[r.tone] || TONES.grey;
        return (
          <div key={e.id} style={{ background: tone.bg,
            border: `1.5px solid ${tone.border}`, borderRadius: 10, padding: "12px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between",
              gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: YD,
                fontFamily: "'Space Mono',monospace", textTransform: "uppercase",
                letterSpacing: ".05em" }}>
                {e.kind || "send"} · {e.channel || "whatsapp"}
              </span>
              <span style={{ fontSize: 11.5, color: SUB }}>{tsAgo(e.at)}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10,
              flexWrap: "wrap", marginBottom: 6 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5,
                fontSize: 11.5, fontWeight: 900, padding: "3px 9px", borderRadius: 999,
                background: tone.chip, color: "#fff",
                fontFamily: "'Space Mono',monospace", textTransform: "uppercase",
                letterSpacing: ".05em" }}>
                {r.tone === "green" && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="5"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {r.tone === "red" && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path d="M6 6l12 12M6 18L18 6" stroke="#fff" strokeWidth="4"
                      strokeLinecap="round" />
                  </svg>
                )}
                {r.label}
              </span>
              <span style={{ fontSize: 11.5, color: MID, fontWeight: 700 }}>
                {Array.isArray(e.recipients) ? `${e.recipients.length} recipient${e.recipients.length === 1 ? "" : "s"}` : "—"}
              </span>
            </div>
            <div style={{ fontSize: 13, color: INK, whiteSpace: "pre-wrap",
              lineHeight: 1.5, maxHeight: 90, overflow: "hidden" }}>
              {e.text || "(media-only)"}
            </div>
            {Array.isArray(e.attachments) && e.attachments.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 11.5, color: SUB, fontWeight: 700 }}>
                attachments: {e.attachments.map(a => a.name).join(", ")}
              </div>
            )}
            {r.tone === "mixed" && r.detail && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ fontSize: 11.5, color: tone.fg, fontWeight: 800,
                  cursor: "pointer" }}>See per-recipient result</summary>
                <div style={{ marginTop: 6, fontSize: 11.5,
                  fontFamily: "'Space Mono',monospace", color: MID,
                  background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 6,
                  padding: "8px 10px" }}>
                  {r.detail.map((d, i) => (
                    <div key={i} style={{ color: d.ok ? "#15803D" : "#B91C1C" }}>
                      {d.ok ? "OK " : "FAIL "}{d.phone || d.group || ""} {d.error ? "· " + d.error : ""}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   REMINDERS TAB — auto-nudge config + on/off toggles + run-now.
   ────────────────────────────────────────────────────────────────────── */
const REMINDER_DEFS = [
  { key: "onbStuck", label: "Onboarding stalled",
    desc: "DM the student if their onboarding form isn't complete after N days from the email.",
    unit: "days", defaults: { enabled: true, days: 3, cooldownDays: 3 } },
  { key: "joinMissing", label: "Group join missing",
    desc: "DM anyone (student / mom / dad) who hasn't joined the WhatsApp group N hours after it was created.",
    unit: "hours", defaults: { enabled: true, hours: 24, cooldownDays: 2 } },
  { key: "profileMeeting", label: "Profile meeting not booked",
    desc: "Ping Ravisha if a student finished onboarding N days ago but the profile meeting still isn't marked done.",
    unit: "days", defaults: { enabled: true, days: 5, cooldownDays: 4 } },
  { key: "deadline", label: "Deadline approaching",
    desc: "DM the student when any college's NM deadline lands within N days.",
    unit: "days", defaults: { enabled: true, days: 7, cooldownDays: 5 } },
];

function RemindersTab({ settings }) {
  const [cfg, setCfg] = useState(null);
  const [paused, setPaused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ran, setRan] = useState(false);

  useEffect(() => {
    const r = (settings && settings.reminders) || {};
    const merged = {};
    REMINDER_DEFS.forEach(d => { merged[d.key] = { ...d.defaults, ...(r[d.key] || {}) }; });
    setCfg(merged);
    setPaused(!!(settings && settings.remindersPaused));
  }, [settings]);

  async function persist(next, nextPaused) {
    await setDoc(doc(db, "settings", "whatsapp_ravisha"),
      { reminders: next, remindersPaused: nextPaused, updatedAt: serverTimestamp() },
      { merge: true });
  }
  function setField(key, field, value) {
    const next = { ...cfg, [key]: { ...cfg[key], [field]: value } };
    setCfg(next);
    persist(next, paused);
  }
  async function togglePause() {
    const np = !paused; setPaused(np); await persist(cfg, np);
  }
  async function runNow() {
    setBusy(true);
    try { await queueRemindersRun(); setRan(true); setTimeout(() => setRan(false), 4000); }
    finally { setBusy(false); }
  }

  if (!cfg) return <div style={{ fontSize: 13, color: SUB }}>Loading…</div>;

  const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 18 };
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ ...card, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: INK }}>Automatic reminders</div>
          <div style={{ fontSize: 12.5, color: MID, marginTop: 3 }}>
            The bot sweeps every 6 hours and sends the nudges below. Each type has a cooldown so
            no one is messaged twice in a row.
          </div>
        </div>
        <button onClick={togglePause}
          style={{ fontSize: 12.5, fontWeight: 800, padding: "8px 16px", borderRadius: 8,
            border: "none", cursor: "pointer",
            background: paused ? "#FEE2E2" : "#DCFCE7",
            color: paused ? "#B91C1C" : "#15803D" }}>
          {paused ? "Paused — resume" : "Active — pause all"}
        </button>
        <button onClick={runNow} disabled={busy}
          style={{ fontSize: 12.5, fontWeight: 800, padding: "8px 16px", borderRadius: 8,
            border: "none", cursor: "pointer", background: WA, color: "#fff",
            opacity: busy ? 0.6 : 1 }}>
          {ran ? "Queued ✓" : busy ? "…" : "Run now"}
        </button>
      </div>

      {REMINDER_DEFS.map(d => {
        const c = cfg[d.key];
        const on = c.enabled && !paused;
        return (
          <div key={d.key} style={{ ...card, opacity: paused ? 0.6 : 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: INK }}>{d.label}</div>
                <div style={{ fontSize: 12.5, color: MID, marginTop: 3, lineHeight: 1.5 }}>{d.desc}</div>
              </div>
              <button onClick={() => setField(d.key, "enabled", !c.enabled)}
                style={{ width: 46, height: 26, borderRadius: 999, border: "none", flexShrink: 0,
                  background: on ? "#16A34A" : "#CBD5E1", cursor: "pointer", position: "relative",
                  transition: ".15s" }}>
                <span style={{ position: "absolute", top: 3, left: on ? 23 : 3, width: 20, height: 20,
                  borderRadius: 999, background: "#fff", transition: ".15s" }} />
              </button>
            </div>
            <div style={{ display: "flex", gap: 18, marginTop: 12, flexWrap: "wrap" }}>
              <label style={{ fontSize: 12.5, fontWeight: 700, color: MID,
                display: "flex", alignItems: "center", gap: 7 }}>
                Trigger after
                <input type="number" min="1" value={c[d.unit] || ""}
                  onChange={e => setField(d.key, d.unit, Math.max(1, +e.target.value || 1))}
                  style={{ width: 60, padding: "6px 8px", fontSize: 13, fontWeight: 700,
                    border: `1.5px solid ${BORDER}`, borderRadius: 7, textAlign: "center" }} />
                {d.unit}
              </label>
              <label style={{ fontSize: 12.5, fontWeight: 700, color: MID,
                display: "flex", alignItems: "center", gap: 7 }}>
                Cooldown
                <input type="number" min="1" value={c.cooldownDays || ""}
                  onChange={e => setField(d.key, "cooldownDays", Math.max(1, +e.target.value || 1))}
                  style={{ width: 60, padding: "6px 8px", fontSize: 13, fontWeight: 700,
                    border: `1.5px solid ${BORDER}`, borderRadius: 7, textAlign: "center" }} />
                days
              </label>
            </div>
          </div>
        );
      })}
      <div style={{ fontSize: 12, color: SUB, fontWeight: 600 }}>
        Reminder copy is pulled from the Templates tab (Onboarding reminder, Group-join chase,
        Profile-meeting nudge, Deadline reminder). Edit the wording there.
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
   ROOT
   ────────────────────────────────────────────────────────────────────── */
export default function RavishaHub({ students, onBack }) {
  const [tab, setTab] = useState("Compose");
  const [settings, setSettings] = useState(null);
  const [waStatus, setWaStatus] = useState(null);
  const [templates, setTemplatesState] = useState(DEFAULT_TEMPLATES);
  const [pics, setPics] = useState({});

  useEffect(() => {
    return onSnapshot(doc(db, "settings", "whatsapp_ravisha"), snap => {
      const d = snap.exists() ? snap.data() : {};
      setSettings(d);
      setWaStatus(d.status || "loading");
      if (d.templates) {
        const merged = DEFAULT_TEMPLATES.map(t => ({
          ...t, ...(d.templates[t.key] || {}), key: t.key,
        }));
        setTemplatesState(merged);
      }
      if (d.groupPics) setPics(d.groupPics);
    });
  }, []);

  const connected = waStatus === "connected";
  const tabs = ["Compose", "Templates", "Reminders", "Group pics", "History"];

  return (
    <div style={{ maxWidth: 1240, margin: "0 auto", padding: "22px 26px 60px" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: SUB,
        fontSize: 13, fontWeight: 700, cursor: "pointer", padding: 0, marginBottom: 14 }}>
        ← Back to TUAP students
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
        marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900, color: INK,
          letterSpacing: "-.02em" }}>Communications</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px",
          borderRadius: 999, background: connected ? "#DCFCE7" : "#FEF3C7",
          color: connected ? GREEN : YD, fontSize: 11.5, fontWeight: 800,
          fontFamily: "'Space Mono',monospace", textTransform: "uppercase",
          letterSpacing: ".04em" }}>
          <span style={{ width: 8, height: 8, borderRadius: 999,
            background: connected ? GREEN : YD }} />
          {connected ? `Ravisha · +${settings.connectedNumber}` :
            waStatus === "qr_needed" ? "Waiting for QR scan"
            : waStatus || "loading"}
        </div>
        <a href="/ravisha-wa" style={{ fontSize: 12, fontWeight: 800, color: WA,
          textDecoration: "underline" }}>Open QR page</a>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: `1.5px solid ${BORDER}`,
        marginBottom: 20, overflowX: "auto" }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ background: "none", border: "none", padding: "10px 16px",
              fontSize: 13.5, fontWeight: 800,
              color: tab === t ? INK : SUB, cursor: "pointer",
              borderBottom: `3px solid ${tab === t ? Y : "transparent"}`,
              marginBottom: -2 }}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Compose" && <ComposeTab students={students} templates={templates} />}
      {tab === "Templates" && <TemplatesTab templates={templates} setTemplates={setTemplatesState} />}
      {tab === "Reminders" && <RemindersTab settings={settings} />}
      {tab === "Group pics" && <GroupPicsTab pics={pics} setPics={setPics} />}
      {tab === "History" && <HistoryTab />}
    </div>
  );
}
